import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  buscarPedido,
  registrarChegada,
} from '../../../api/previsaoPecas/previsaoPecasApi';
import { Chegada, DetalheDoPedido, ItemDeNotaAIdentificar, ItemDoPedido } from '../../../interfaces/PrevisaoPecas';
import TabelaSistema, { ColunaTabela } from '../../../components/sync-painel/TabelaSistema';
import { notify } from '../../../shared/feedback/notify';
import { useAlcanca } from '../../../hooks/usePapeis';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import BarraDeChegada, { textoDaChegada } from '../components/BarraDeChegada';
import RegistrarChegadaModal from '../components/RegistrarChegadaModal';
import { quemRegistrou } from '../components/PrevisaoItemModal';
import NotaModal from '../components/NotaModal';
import PecaDoPedidoModal from '../components/PecaDoPedidoModal';
import { PREVISAO_PECAS } from '../routes';
import {
  CELULA_DE_UMA_LINHA,
  SITUACAO_DO_PEDIDO,
  SITUACAO_GERENCIAL,
  STATUS_DA_PREVISAO,
  formatarDiaPuro,
  formatarQuantidade,
  rotuloDoStatus,
} from '../formatos';

import '../../recepcao/pages/Recepcao.css';
import '../../rastreamento/pages/RastreamentoPage.css'; // o botão da linha
import '../../../shared/ui/dashboard-base.css'; // cartões, grade e KPIs
import '../../portal_cotacoes/components/ConviteAcoesModal.css'; // a grade rótulo/valor
import '../../admin-sistema/pages/css/adminSistema.css'; // campo do motivo e botões
import '../../../shared/ui/badge-status.css';
import '../../../shared/feedback/erro-de-tela.css';

type ItemComChegadas = DetalheDoPedido['itens'][number];

/**
 * Um pedido de compra com a chegada de cada peça (previsao-pecas/at-02).
 *
 * Nasceu como modal e virou tela a pedido do Victor (24/10/2026): são muitas informações — o
 * pedido, cada peça com o seu saldo, cada chegada com quem registrou, as notas e os itens a
 * identificar — e num modal elas ficavam confusas.
 *
 * O que a tela responde: das peças deste pedido, quanto já chegou, por qual nota, em que dia e
 * QUEM registrou — o SOAC (pela nota do Nasajon) ou uma pessoa.
 *
 * 🔴 Regra da at-60, aqui em dobro: falha de carga mostra a faixa de erro e NADA mais — sem
 * peças, sem "nenhuma chegada", sem botões. Uma tela vazia por erro seria lida como "nada
 * chegou", e alguém registraria à mão uma chegada que a nota já registrou.
 */
const PedidoPage: React.FC = () => {
  const { pedidoId = '' } = useParams<{ pedidoId: string }>();
  const navigate = useNavigate();
  const podeConferir = useAlcanca(['ESTOQUE']);

  const [pedido, setPedido] = useState<DetalheDoPedido | null>(null);
  const [falhou, setFalhou] = useState(false);
  const [itemSelecionadoId, setItemSelecionadoId] = useState<string | null>(null);

  const [registrando, setRegistrando] = useState<{ item: ItemDoPedido; itemDaNota?: ItemDeNotaAIdentificar } | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [escolhaDaNota, setEscolhaDaNota] = useState<Record<string, string>>({});
  const [notaAberta, setNotaAberta] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setPedido(await buscarPedido(pedidoId));
      setFalhou(false);
    } catch (error) {
      setPedido(null);
      setFalhou(true);
      notify.errorFrom(error, 'Não foi possível carregar o pedido. Recarregue a página; se persistir, acione a TI.');
    }
  }, [pedidoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const recarregarDepoisDeGravar = async (aviso: string) => {
    try {
      setPedido(await buscarPedido(pedidoId));
    } catch {
      notify.warning(aviso, { toastId: 'previsao-pecas-pedido-refresh' });
    }
  };

  const salvarChegada = async (dados: { quantidade: number; chegouEm: string }) => {
    if (!registrando) return;
    setSalvando(true);
    let excedente = false;
    try {
      const r = await registrarChegada(pedidoId, registrando.item.id, { ...dados, notaItemId: registrando.itemDaNota?.notaItemId });
      excedente = r.excedente;
    } catch (error) {
      // O modal continua aberto: fechar anunciaria uma chegada que não foi gravada.
      notify.actionError(error, { action: 'create', subject: 'registro de chegada' });
      setSalvando(false);
      return;
    }
    setSalvando(false);
    setItemSelecionadoId(registrando.item.id);
    setPecaDeRetorno(null);
    setRegistrando(null);
    notify.success(
      excedente ? 'Chegada registrada. A quantidade passou do que foi pedido e ficou marcada como excedente.' : 'Chegada registrada.',
    );
    await recarregarDepoisDeGravar('A chegada foi registrada, mas a atualização da tela falhou. Recarregue a página.');
  };

  const itemSelecionado = pedido?.itens.find((i) => i.id === itemSelecionadoId) ?? null;

  const colunas: ColunaTabela<ItemComChegadas>[] = useMemo(
    () => [
      {
        titulo: 'Peça',
        largura: 260,
        larguraMaxima: 320,
        texto: (i) => `${i.produtoDescricao ?? 'Peça sem descrição'}${i.produtoCodigo ? ` (${i.produtoCodigo})` : ''}`,
        render: (i) => (
          <button
            type="button"
            className="rastreamento-placa-botao"
            onClick={() => setItemSelecionadoId(i.id)}
          >
            {i.produtoDescricao ?? 'Peça sem descrição'}
          </button>
        ),
      },
      { titulo: 'Código', largura: 90, larguraMaxima: 110, texto: (i) => i.produtoCodigo ?? '-' },
      {
        titulo: 'Chegada',
        largura: 190,
        larguraMaxima: 240,
        texto: (i) => textoDaChegada(i.chegada, i.pedida, i.unidade ?? 'un'),
        render: (i) => (
          <span style={CELULA_DE_UMA_LINHA}>
            <BarraDeChegada recebida={i.chegada} pedida={i.pedida} unidade={i.unidade ?? 'un'} />
          </span>
        ),
      },
      { titulo: 'Previsão', largura: 100, larguraMaxima: 100, texto: (i) => formatarDiaPuro(i.previsao) },
      {
        titulo: 'Status',
        largura: 150,
        larguraMaxima: 180,
        texto: (i) => rotuloDoStatus(i.status),
        render: (i) => (
          <span className={`cilia-badge ${STATUS_DA_PREVISAO[i.status].classe}`}>{rotuloDoStatus(i.status)}</span>
        ),
      },
      {
        titulo: 'Registros',
        largura: 200,
        larguraMaxima: 260,
        // A última chegada, em uma linha: a lista inteira fica no painel da peça.
        texto: (i) => {
          const ativas = i.chegadas.filter((c) => !c.cancelada);
          if (!ativas.length) return 'nenhum';
          const ultima = ativas[ativas.length - 1];
          return `${ativas.length} · ${quemRegistrou(ultima)}`;
        },
        render: (i) => {
          const ativas = i.chegadas.filter((c) => !c.cancelada);
          const ultima = ativas[ativas.length - 1];
          if (!ultima) return <span style={CELULA_DE_UMA_LINHA}>nenhum</span>;
          return (
            <span style={CELULA_DE_UMA_LINHA}>
              {ativas.length} · <QuemRegistrou chegada={ultima} onAbrirNota={setNotaAberta} />
            </span>
          );
        },
      },
    ],
    [],
  );

  /*
   * O modal da peça FECHA antes de abrir outro (registrar, previsão, nota): o filho ficaria fora do
   * `ref` dele, e o "clicou fora" o derrubaria no primeiro clique. `pecaDeRetorno` guarda a peça
   * para o modal dela voltar quando o filho fechar.
   */
  const [pecaDeRetorno, setPecaDeRetorno] = useState<string | null>(null);
  const saindoDaPeca = () => {
    setPecaDeRetorno(itemSelecionadoId);
    setItemSelecionadoId(null);
  };
  const voltarParaPeca = () => {
    setItemSelecionadoId(pecaDeRetorno);
    setPecaDeRetorno(null);
  };

  const pecasChegaram = pedido ? pedido.itens.filter((i) => i.saldo <= 0).length : 0;

  return (
    <div className="ConteDo recepcao-conte-do">
      <div className="recepcao-header-row">
        <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <span>Pedido {pedido?.numero ?? ''}</span>
          {pedido && (
            <span className={`cilia-badge ${SITUACAO_DO_PEDIDO[pedido.situacaoChegada].classe}`}>
              {SITUACAO_DO_PEDIDO[pedido.situacaoChegada].rotulo}
            </span>
          )}
        </div>
        {/* Sem o wrapper `VisualizarOrdenar`: o padding de 30px dele esticava a linha do cabeçalho
            e o título descia, desalinhado do logo. */}
        <div
          className="CtaBig MouseHover"
          role="button"
          tabIndex={0}
          onClick={() => navigate(PREVISAO_PECAS())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') navigate(PREVISAO_PECAS());
          }}
          style={{
            backgroundColor: 'var(--tema-superficie, white)',
            border: '1px solid var(--tema-linha-2, #CACACA)',
            cursor: 'pointer',
            marginLeft: 'auto',
            marginRight: 0,
          }}
        >
          <div className="Frame1000003987" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src={ArrowLeftOrange} alt="" width="16" height="12" />
            <span style={{ color: 'var(--tema-texto, #1A1C1B)', fontWeight: 600, fontSize: 14, fontFamily: 'AmpleSoft-Bold, sans-serif' }}>
              Voltar à lista
            </span>
          </div>
        </div>
      </div>

      {falhou && (
        <div className="erro-de-tela" role="alert">
          Não foi possível carregar o pedido. Isso não quer dizer que nada chegou. Recarregue a página; se persistir,
          acione a TI.
        </div>
      )}
      {!falhou && !pedido && <p className="do-nota-janela">Carregando...</p>}

      {pedido && (
        <>
          <div className="do-kpis" style={{ width: '100%' }}>
            <div className="do-kpi">
              <div className="do-kpi-rotulo">Peças que chegaram</div>
              <div className="do-kpi-valor">
                {pecasChegaram} de {pedido.itens.length}
              </div>
              <div className="do-kpi-apoio">{pedido.itens.length === 0 ? 'itens ainda não lidos do Nasajon' : 'na quantidade pedida'}</div>
            </div>
            <div className="do-kpi">
              <div className="do-kpi-rotulo">Última chegada</div>
              <div className="do-kpi-valor">{formatarDiaPuro(pedido.ultimaChegadaEm, '—')}</div>
              <div className="do-kpi-apoio">{pedido.notas.length} nota(s) de entrada</div>
            </div>
            <div className="do-kpi">
              <div className="do-kpi-rotulo">Previsão do pedido</div>
              <div className="do-kpi-valor">{formatarDiaPuro(pedido.previsao, '—')}</div>
              <div className="do-kpi-apoio">{pedido.atrasado ? 'vencida sem chegada completa' : 'informada pelo fornecedor'}</div>
            </div>
            <div className="do-kpi">
              <div className="do-kpi-rotulo">Situação no Nasajon</div>
              <div className="do-kpi-valor" style={{ fontSize: 18 }}>
                {pedido.situacaoGerencial ? (SITUACAO_GERENCIAL[pedido.situacaoGerencial] ?? pedido.situacaoGerencial) : 'Não sincronizada'}
              </div>
              <div className="do-kpi-apoio">não decide a chegada: quem decide é o saldo das peças</div>
            </div>
          </div>

          <div className="do-grid">
            <div className="do-card">
              <h3 className="do-card-titulo">Pedido</h3>
              <p className="do-card-descricao">Dados do pedido no Nasajon e da requisição que o originou.</p>
              <div className="convite-modal-grade">
                <span className="convite-modal-rotulo">Fornecedor</span>
                <span>{pedido.fornecedorNome ?? 'não identificado'}</span>
                <span className="convite-modal-rotulo">Emissão</span>
                <span>{formatarDiaPuro(pedido.emissao, 'não informada')}</span>
                <span className="convite-modal-rotulo">Requisição</span>
                <span>{pedido.requisicaoCodigo ?? 'sem requisição de origem'}</span>
                <span className="convite-modal-rotulo">Comprador</span>
                <span>{pedido.compradorNome ?? 'não identificado'}</span>
              </div>
            </div>
            <div className="do-card">
              <h3 className="do-card-titulo">Destino</h3>
              <p className="do-card-descricao">Para onde as peças vão — vem da requisição de origem.</p>
              <div className="convite-modal-grade">
                <span className="convite-modal-rotulo">Veículo</span>
                <span>{pedido.placa ?? 'sem placa informada'}</span>
                <span className="convite-modal-rotulo">Oficina</span>
                <span>
                  {pedido.oficina ?? 'não informada'}
                  {pedido.oficinaExterna ? ' (oficina externa)' : ''}
                </span>
              </div>
            </div>
          </div>

          <div className="do-grid-largo">
            <div className="do-card">
              <TabelaSistema colunas={colunas} itens={pedido.itens} chave={(i) => i.id} vazio="Os itens deste pedido ainda não foram lidos do Nasajon." />
            </div>
          </div>

          {pedido.itensDeNotaAIdentificar.length > 0 && (
            <div className="do-grid-largo">
              <div className="do-card">
                <h3 className="do-card-titulo">Itens da nota a identificar</h3>
                <p className="do-card-descricao">
                  A nota trouxe estes itens, mas o produto não corresponde a nenhuma peça dos pedidos ligados a ela — por
                  exemplo, quando o fornecedor troca o código. Diga a qual peça cada um corresponde.
                </p>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
                  {pedido.itensDeNotaAIdentificar.map((n) => (
                    <li key={n.notaItemId} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', fontSize: 13 }}>
                      <span>
                        Nota {n.notaNumero ?? 'sem número'}:{' '}
                        <strong>
                          {formatarQuantidade(n.quantidade ?? 0)} {n.unidade ?? ''} {n.descricao ?? 'item sem descrição'}
                        </strong>
                        {n.codigo ? ` (${n.codigo})` : ''}
                      </span>
                      {podeConferir && (
                        <>
                          <select
                            className="admin-select"
                            aria-label={`Peça do pedido para o item ${n.descricao ?? n.notaItemId}`}
                            value={escolhaDaNota[n.notaItemId] ?? ''}
                            onChange={(e) => setEscolhaDaNota((atual) => ({ ...atual, [n.notaItemId]: e.target.value }))}
                          >
                            <option value="">Escolha a peça do pedido</option>
                            {pedido.itens.map((i) => (
                              <option key={i.id} value={i.id}>
                                {i.produtoDescricao ?? 'Peça sem descrição'}
                                {i.produtoCodigo ? ` (${i.produtoCodigo})` : ''}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="admin-btn admin-btn-primary"
                            disabled={!escolhaDaNota[n.notaItemId]}
                            onClick={() => {
                              const item = pedido.itens.find((i) => i.id === escolhaDaNota[n.notaItemId]);
                              if (item) setRegistrando({ item, itemDaNota: n });
                            }}
                          >
                            Identificar
                          </button>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="do-grid-largo">
            <div className="do-card">
              <h3 className="do-card-titulo">Notas de entrada</h3>
              <p className="do-card-descricao">As notas fiscais ligadas a este pedido no Nasajon. É delas que a chegada automática sai.</p>
              {pedido.notas.length === 0 ? (
                <p className="do-hint-campo">Nenhuma nota de entrada ligada a este pedido no Nasajon.</p>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6, fontSize: 13 }}>
                  {pedido.notas.map((n) => (
                    <li key={n.notaId}>
                      Nota <strong>{n.numero ?? 'sem número'}</strong> — entrada em {formatarDiaPuro(n.dataEntrada, 'data ainda não lida')}
                      {n.fornecedorNome ? ` — ${n.fornecedorNome}` : ''}{' '}
                      <button type="button" className="do-celula-link" onClick={() => setNotaAberta(n.notaId)}>
                        ver itens
                      </button>
                      {!n.lida ? ' (itens ainda não lidos do Nasajon)' : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}

      {registrando && pedido && (
        <RegistrarChegadaModal
          item={registrando.item}
          pedidoNumero={pedido.numero}
          itemDaNota={registrando.itemDaNota}
          salvando={salvando}
          onConfirmar={salvarChegada}
          onFechar={() => {
            setRegistrando(null);
            voltarParaPeca();
          }}
        />
      )}

      {notaAberta && (
        <NotaModal
          notaId={notaAberta}
          onFechar={() => {
            setNotaAberta(null);
            voltarParaPeca();
          }}
        />
      )}

      {itemSelecionado && pedido && (
        <PecaDoPedidoModal
          item={itemSelecionado}
          pedidoNumero={pedido.numero}
          podeConferir={podeConferir}
          onRegistrarChegada={() => {
            saindoDaPeca();
            setRegistrando({ item: itemSelecionado });
          }}
          onAbrirNota={(notaId) => {
            saindoDaPeca();
            setNotaAberta(notaId);
          }}
          onCancelado={() =>
            recarregarDepoisDeGravar('O cancelamento foi feito, mas a atualização da tela falhou. Recarregue a página.')
          }
          onFechar={() => setItemSelecionadoId(null)}
        />
      )}
    </div>
  );
};

/**
 * "SOAC, pela nota N" com o número da nota clicável (abre o que a nota trouxe), ou o nome de quem
 * registrou. O texto é o mesmo de `quemRegistrou` — só a nota vira botão.
 */
const QuemRegistrou: React.FC<{ chegada: Chegada; onAbrirNota: (notaId: string) => void }> = ({ chegada, onAbrirNota }) => {
  if (chegada.origem !== 'NOTA' || !chegada.notaId) return <>{quemRegistrou(chegada)}</>;
  const notaId = chegada.notaId;
  return (
    <>
      SOAC, pela nota{' '}
      <button
        type="button"
        className="do-celula-link"
        onClick={(e) => {
          e.stopPropagation();
          onAbrirNota(notaId);
        }}
      >
        {chegada.notaNumero ?? 'sem número'}
      </button>
    </>
  );
};

export default PedidoPage;
